import { jsPDF } from "jspdf";
import { parseItemCards } from "../pages/gate1/testStrategyCards";

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 18;
const MARGIN_TOP = 18;
const MARGIN_BOTTOM = 20;
const CONTENT_W = PAGE_W - MARGIN_X * 2;

const COLOR = {
  navy: [14, 40, 65],
  teal: [21, 96, 130],
  heading: [46, 116, 181],
  heading3: [31, 77, 120],
  text: [0, 0, 0],
  muted: [85, 85, 85],
  border: [232, 232, 232],
  white: [255, 255, 255],
  footer: [70, 120, 134],
};

const CANONICAL_NUMBERS = {
  approved_context_reference: 0,
  validation_scope: 1,
  validation_architecture: 2,
  validation_intensity: 3,
  validation_scenarios: 4,
  validation_planning_profile: 5,
};

function numberedSections(sections) {
  let nextCustom = 6;
  return (sections || []).map((section) => {
    const id = String(section?.id || "").trim().toLowerCase();
    const num = Object.prototype.hasOwnProperty.call(CANONICAL_NUMBERS, id)
      ? CANONICAL_NUMBERS[id]
      : nextCustom++;
    return { section, num };
  });
}

function linesFromText(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  return raw.split(/\n+/).map((l) => l.trim()).filter(Boolean);
}

class StrategyPdfWriter {
  constructor() {
    this.pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    this.y = MARGIN_TOP;
    this.page = 1;
    this.drawFooter();
  }

  setColor(rgb) {
    this.pdf.setTextColor(rgb[0], rgb[1], rgb[2]);
  }

  setFill(rgb) {
    this.pdf.setFillColor(rgb[0], rgb[1], rgb[2]);
  }

  setDraw(rgb) {
    this.pdf.setDrawColor(rgb[0], rgb[1], rgb[2]);
  }

  remaining() {
    return PAGE_H - MARGIN_BOTTOM - this.y;
  }

  addPage() {
    this.pdf.addPage();
    this.page += 1;
    this.y = MARGIN_TOP;
    this.drawFooter();
  }

  ensure(neededMm) {
    if (this.remaining() < neededMm) this.addPage();
  }

  drawFooter() {
    this.pdf.setFont("helvetica", "normal");
    this.pdf.setFontSize(8);
    this.setColor(COLOR.footer);
    this.setDraw(COLOR.border);
    this.pdf.setLineWidth(0.2);
    this.pdf.line(MARGIN_X, PAGE_H - 14, PAGE_W - MARGIN_X, PAGE_H - 14);
    this.pdf.text("Generated by ValidAite Assurance Engine", MARGIN_X, PAGE_H - 9);
    this.pdf.text(String(this.page), PAGE_W - MARGIN_X, PAGE_H - 9, { align: "right" });
  }

  writeLines(lines, { size, style = "normal", color = COLOR.text, lineH, after = 2, x = MARGIN_X, maxW = CONTENT_W }) {
    this.pdf.setFont("helvetica", style);
    this.pdf.setFontSize(size);
    this.setColor(color);
    const lh = lineH ?? size * 0.42;
    lines.forEach((line) => {
      const wrapped = this.pdf.splitTextToSize(String(line), maxW);
      wrapped.forEach((w) => {
        this.ensure(lh + 1);
        this.pdf.text(w, x, this.y + size * 0.35);
        this.y += lh;
      });
    });
    this.y += after;
  }

  heading(text, { size, color, after = 4, before = 6, style = "bold" } = {}) {
    const lh = size * 0.45;
    this.ensure(before + lh + 4);
    this.y += before;
    this.writeLines([text], { size, style, color, lineH: lh, after });
  }

  paragraph(text, opts = {}) {
    const lines = linesFromText(text);
    if (!lines.length) return;
    this.writeLines(lines, {
      size: opts.size ?? 11,
      style: opts.style ?? "normal",
      color: opts.color ?? COLOR.text,
      lineH: opts.lineH ?? 5.2,
      after: opts.after ?? 2.5,
      x: opts.x,
      maxW: opts.maxW,
    });
  }

  metaTable(rows) {
    const labelW = 58;
    const valueW = CONTENT_W - labelW;
    const pad = 2.2;
    const lineH = 4.6;

    rows.forEach(([label, value]) => {
      this.pdf.setFont("helvetica", "normal");
      this.pdf.setFontSize(9.5);
      const valueLines = this.pdf.splitTextToSize(String(value || "—"), valueW - pad * 2);
      const rowH = Math.max(8, valueLines.length * lineH + pad * 2);
      this.ensure(rowH + 1);

      this.setFill(COLOR.teal);
      this.setDraw(COLOR.border);
      this.pdf.rect(MARGIN_X, this.y, labelW, rowH, "F");
      this.pdf.rect(MARGIN_X + labelW, this.y, valueW, rowH, "S");

      this.pdf.setFont("helvetica", "bold");
      this.pdf.setFontSize(9.5);
      this.setColor(COLOR.white);
      this.pdf.text(label, MARGIN_X + pad, this.y + pad + 3.6);

      this.pdf.setFont("helvetica", "normal");
      this.setColor(COLOR.text);
      valueLines.forEach((line, i) => {
        this.pdf.text(line, MARGIN_X + labelW + pad, this.y + pad + 3.6 + i * lineH);
      });

      this.y += rowH;
    });
    this.y += 6;
  }

  coverAndToc(meta, listed, aiSystem) {
    this.heading("VALIDAITE", { size: 18, color: COLOR.navy, before: 0, after: 1 });
    this.paragraph("AI Assurance & Validation Platform", {
      size: 10,
      color: COLOR.teal,
      lineH: 5,
      after: 8,
    });
    this.heading("Validation Strategy", { size: 26, color: COLOR.navy, before: 0, after: 3 });
    this.heading(aiSystem, { size: 14, color: COLOR.navy, before: 0, after: 6 });

    this.metaTable([
      ["Document Type", meta.documentType || "Validation Strategy (Auto-Generated)"],
      ["AI System", aiSystem],
      ["Generated Following", meta.generatedFollowing || "Gate 0 (System Context) and Gate 1"],
      ["Approval Status", meta.approvalStatus || "Pending TEVV Sign-off"],
      ["Generated By", "ValidAite Assurance Engine"],
      ["Document Owner", meta.documentOwner || "TEVV Lead / AI Assurance Lead"],
      ["Classification", meta.classification || "Governance & Program Distribution"],
    ]);

    this.heading("Contents", { size: 16, color: COLOR.heading, before: 2, after: 3 });
    listed.forEach(({ section, num }) => {
      const title = section?.title || "Untitled section";
      this.paragraph(`${num}.  ${title.toUpperCase()}`, {
        size: 11,
        style: "bold",
        color: COLOR.navy,
        lineH: 5.2,
        after: 1,
      });
      (section?.items || []).forEach((item, j) => {
        this.paragraph(`${num}.${j + 1}  ${item.heading || `Item ${j + 1}`}`, {
          size: 10,
          color: COLOR.muted,
          lineH: 4.6,
          after: 0.6,
          x: MARGIN_X + 5,
          maxW: CONTENT_W - 5,
        });
      });
    });
  }

  writeItem(item, sectionNum, itemIndex) {
    const heading = item?.heading || `Item ${itemIndex + 1}`;
    this.heading(`${sectionNum}.${itemIndex + 1}  ${heading}`, {
      size: 13,
      color: COLOR.heading,
      before: 5,
      after: 2,
    });

    const cards = parseItemCards(item);
    if (cards.length && (cards.length > 1 || cards[0]?.title)) {
      cards.forEach((card) => {
        if (card.title) {
          this.heading(card.title, { size: 12, color: COLOR.heading3, before: 2, after: 1.5 });
        }
        this.paragraph(card.text);
      });
      return;
    }
    this.paragraph(item?.body);
  }

  writeSections(listed) {
    listed.forEach(({ section, num }) => {
      const title = section?.title || "Untitled section";
      this.heading(`${num}.  ${title.toUpperCase()}`, {
        size: 16,
        color: COLOR.heading,
        before: 8,
        after: 3,
      });
      if (section?.summary) this.paragraph(section.summary);
      (section?.items || []).forEach((item, j) => this.writeItem(item, num, j));
    });
  }

  save(filename) {
    this.pdf.save(filename);
  }
}

export function testStrategyPdfFilename(aiSystem) {
  const base = String(aiSystem || "Validation_Strategy")
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_");
  return `${base || "Validation_Strategy"}_Validation_Strategy.pdf`;
}

/**
 * Download the full Test Strategy as a clean, paginated PDF
 * styled like Validation_Strategy_v3.0.
 */
export async function exportTestStrategyPdf({ documentMeta, sections }) {
  const meta = documentMeta || {};
  const listed = numberedSections(sections);
  const aiSystem = meta.aiSystem || "AI System";
  const writer = new StrategyPdfWriter();
  writer.coverAndToc(meta, listed, aiSystem);
  writer.writeSections(listed);
  writer.save(testStrategyPdfFilename(aiSystem));
}
